import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';
import {
  change,
  stopSubmit,
  Field,
  FieldArray,
  reduxForm,
  untouch,
  updateSyncErrors
} from 'redux-form';
import { useQuery } from '@apollo/react-hooks';

import Dialog from '@material-ui/core/Dialog';
import { Typography } from '@material-ui/core';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';

import { ACCOMMODATION, BOOK_DATES } from 'graphql/queries/accommodations';

import ConfirmContext from 'contexts/ConfirmContext';

import {
  BookInfoList,
  CircularLoader,
  CustomDateRangePicker
} from 'components';

import Header from 'pages/widget/components/header/Header';
import Footer from 'pages/widget/components/footer/Footer';
import Visitors from 'pages/widget/components/visiters/Visiters';

import {
  CATEGORY_PRICES,
  DAILY_PRICES,
  FIXED_PRICES,
  FUTURE_PRICES
} from 'pages/widget/constants';

import {
  getAvailableRangeDates as getAccommodationAvailableRangeDates,
  getDisabledDates as getAccommodationDisabledDates
} from 'utils/book-accommodation/availableDates';
import { getReceivedAccommodationData } from 'utils/accommodations-and-activities/prepareReceivedAccommodationData';
import { getParticipants as getAccommodationParticipants } from 'utils/book-accommodation/prepareParticipants';
import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import AddOnsList from 'pages/book-activity/components/add-ons-list/AddOnsList';

import validate from './validate';

import { useStyles } from '../styles';

const AccommodationDialog = ({
  open,
  activityData,
  closeDialogCallback,
  initialValues,
  widgetForm,
  changeFormFieldValue,
  stopSubmitForm,
  untouchFormFields,
  handleSubmit,
  ownerId,
  updateFormErrors,
  logo
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const confirm = React.useContext(ConfirmContext);

  const currency = widgetForm.values.currency;
  const accommodationIndex = initialValues.index;
  const reduxAccommodations = widgetForm.values.activities;
  const reduxAccommodation = reduxAccommodations[accommodationIndex];

  const [accommodation, setAccommodation] = React.useState(null);
  const [previousSelectedIndex, setPreviousSelectedIndex] = React.useState(
    null
  );
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const [isSelected, setIsSelected] = React.useState(false);

  const dates = reduxAccommodation.dates;
  const startDate = (dates && dates.startDate) || moment();

  React.useLayoutEffect(() => {
    const updateSize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const { loading, data } = useQuery(ACCOMMODATION, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      accommodationId: initialValues.id,
      businessOwnerId: ownerId
    }
  });

  const {
    loading: bookDatesLoading,
    data: bookDatesData,
    refetch: bookDatesRefetch
  } = useQuery(BOOK_DATES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      businessOwnerId: ownerId,
      accommodationId: initialValues.id,
      startPeriod: moment(startDate)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(startDate).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    }
  });

  React.useEffect(() => {
    setIsSelected(reduxAccommodation.isSelected);
  }, []);

  React.useEffect(() => {
    if (!data || !data.accommodations.length) return;

    const receivedAccommodation = data.accommodations[0];
    const accommodationData = getReceivedAccommodationData(
      receivedAccommodation
    );
    const firstPrice = receivedAccommodation.prices[0];
    const futurePrice = receivedAccommodation ?.futurePrices[0];


    if(futurePrice ?.ratePlanPrices) {
       accommodationData['pricesType'] = FUTURE_PRICES;
    }else if (firstPrice.participant.participant) {
      accommodationData['pricesType'] = CATEGORY_PRICES;
    } else if (firstPrice.day) {
      accommodationData['pricesType'] = DAILY_PRICES;
    } else {
      accommodationData['pricesType'] = FIXED_PRICES;
    }

    setMaxGuests(accommodationData);
    setParticipants(accommodationData);

    const previousIndex = reduxAccommodations.findIndex(
      item => item.isSelected
    );

    if (
      (previousIndex || previousIndex === 0) &&
      previousIndex !== accommodationIndex
    ) {
      changeFormFieldValue(`activities[${previousIndex}].isSelected`, false);
    }

    changeFormFieldValue(`activities[${accommodationIndex}].isSelected`, true);

    changeFormFieldValue(
      `activities[${accommodationIndex}].minNights`,
      accommodationData.generalForm.minNights
    );

    setAddOns(accommodationData);

    setPreviousSelectedIndex(previousIndex);
    setAccommodation(accommodationData);
  }, [data]);

  useEffect(() => {
    if (!accommodation || !reduxAccommodation) return;
    setParticipants(accommodation);
  }, [reduxAccommodation.dates]);

  const setAddOns = accommodationData => {
    if (!reduxAccommodation.addOns || !reduxAccommodation.addOns.length) {
      const addOnsList = accommodationData.addOnsForm.addOns.map(item => {
        return {
          ...item,
          value: 0
        };
      });
      changeFormFieldValue(
        `activities[${accommodationIndex}].addOns`,
        addOnsList
      );
    }
  };

  const setMaxGuests = accommodation => {
    if (!accommodation) return;

    const guests = accommodation.generalForm.guests;
    changeFormFieldValue(
      `activities[${accommodationIndex}].maxParticipants`,
      guests
    );
  };

  const setParticipants = accommodation => {
    if (!accommodation) return;

    const currency = widgetForm.values.currency;
    const dates = reduxAccommodation.dates;
    const previousParticipants = reduxAccommodation.participants || [];

    if (dates && dates.startDate && dates.endDate) {
      const participants = getAccommodationParticipants(
        accommodation,
        currency,
        previousParticipants,
        dates
      );

      changeFormFieldValue(
        `activities[${accommodationIndex}].participants`,
        participants
      );
    } else {
      changeFormFieldValue(
        `activities[${accommodationIndex}].participants`,
        []
      );
    }
  };

  const changeMonthHandler = date => {
    bookDatesRefetch({
      businessOwnerId: ownerId,
      accommodationId: initialValues.id,
      startPeriod: moment(date)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(date).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    });
  };

  const cancelAccommodation = isRemove => {
    const title = isRemove
      ? 'Are you sure you want to remove it?'
      : 'Are you sure you want to cancel it?';
    const confirmationText = isRemove ? 'Remove' : 'Cancel';
    confirm({
      title: title,
      confirmationText: confirmationText,
      cancellationText: 'Back'
    })
      .then(() => {
        changeFormFieldValue(`activities[${accommodationIndex}].dates`, null);
        changeFormFieldValue(
          `activities[${accommodationIndex}].participants`,
          []
        );

        changeFormFieldValue(
          `activities[${accommodationIndex}].isSelected`,
          false
        );

        if (
          (previousSelectedIndex || previousSelectedIndex === 0) &&
          previousSelectedIndex !== accommodationIndex
        ) {
          changeFormFieldValue(
            `activities[${previousSelectedIndex}].isSelected`,
            true
          );
        }

        untouchFormFields([`activities[${accommodationIndex}].dates`]);
        updateFormErrors();
        stopSubmitForm();

        closeDialogCallback();
      })
      .catch(() => {});
  };

  const saveActivity = () => {
    if (widgetForm.syncErrors) return;

    if (
      (previousSelectedIndex || previousSelectedIndex === 0) &&
      previousSelectedIndex !== accommodationIndex
    ) {
      untouchFormFields([`activities[${previousSelectedIndex}].dates`]);
      stopSubmitForm();
    }

    const accommodations = reduxAccommodations.map((item, index) => {
      item.isSelected = index === accommodationIndex;

      if (item.isSelected) return item;

      return {
        ...item,
        dates: null,
        participants: []
      };
    });

    changeFormFieldValue(`activities`, [...accommodations]);
    closeDialogCallback();
  };

  const currentLanguageGeneralForm = accommodation
    ? getFormByCurrentLanguage(
        i18n.language,
        accommodation.generalForm,
        accommodation['generalForm-Norwegian']
      )
    : {};

  return (
    <Dialog open={open} classes={{ paper: classes.paper }}>
      <form onSubmit={handleSubmit(saveActivity)} className={classes.form}>
        <CircularLoader
          isLoading={loading || bookDatesLoading}
          loaderClassName={classes.loaderClassName}
        />

        <Header
          logo={logo}
          cancelCallback={cancelAccommodation}
          title={t('Book your accommodation')}
        />

        {accommodation && (
          <div className={classes.content}>
            <Typography variant="h2" classes={{ root: classes.title }}>
              {currentLanguageGeneralForm.title}
            </Typography>
            <div className={classes.location}>
              <LocationOnOutlinedIcon className={classes.locationIcon} />
              <Typography variant="body1" className={classes.locationText}>
                {accommodation.generalForm.location}
              </Typography>
            </div>
            <div className={classes.editSection}>
              <div className={classNames(classes.editItem, classes.rangeDates)}>
                <Field
                  name={`activities[${accommodationIndex}].dates`}
                  availableRange={getAccommodationAvailableRangeDates(
                    accommodation
                  )}
                  disabledDates={getAccommodationDisabledDates(bookDatesData)}
                  changeMonthsCallback={changeMonthHandler}
                  inputClassName={classes.disableInputFocus}
                  label="Select dates"
                  initialFocus="startDate"
                  dateRangePickerClass="dateRangeOpenedPicker"
                  numberOfMonths={
                    windowWidth > 1010 || windowWidth <= 750 ? 1 : 2
                  }
                  daySize={windowWidth > 600 ? 38 : 41}
                  isHideDatesLabel={true}
                  secondLabelClass={classes.label}
                  labelTypographyVariant="h3"
                  component={CustomDateRangePicker}
                  accomodationDialog
                />
              </div>
              <div className={classes.editItem}>
                <FieldArray
                  name={`activities[${accommodationIndex}].participants`}
                  title="Guests"
                  currency={currency}
                  wrapperClass={classes.visitorsWrapperClass}
                  itemClass={classes.visitItem}
                  titleClass={classes.label}
                  component={Visitors}
                />
              </div>
            </div>

            <div>
              {reduxAccommodation.addOns && !!reduxAccommodation.addOns.length && (
                <Typography
                  variant="h3"
                  classes={{ root: classes.sectionTitleIndent }}
                >
                  {t('Add-ons')}
                </Typography>
              )}
              <FieldArray
                name={`activities[${accommodationIndex}].addOns`}
                currency={currency}
                wrapperClass={classes.addOnWrapperClass}
                addOnItemClass={classes.addOnItemClass}
                addOnDescClass={classes.addOnDescClass}
                isWidget={true}
                component={AddOnsList}
              />
            </div>
            <BookInfoList
              activity={accommodation}
              currency={currency}
              defaultExpanded={false}
              firstDefaultExpanded={true}
              isOverview={true}
              isImage={true}
            />
          </div>
        )}

        <Footer
          isHiddenNavigation={true}
          isAccommodationBooking={false}
          isSelectedProduct={false}
          cancelButton={
            isSelected ? 'Remove Accommodation' : 'Cancel Accommodation'
          }
          confirmButton={
            isSelected ? 'Save Accommodation' : 'Add Accommodation'
          }
          cancelCallback={() => cancelAccommodation(isSelected)}
          accommodationData={activityData}
        />
      </form>
    </Dialog>
  );
};

const mapStateToProps = state => {
  return {
    widgetForm: state.form.widgetForm
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('widgetForm', field, value));
  },
  stopSubmitForm: () => {
    dispatch(stopSubmit('widgetForm', {}));
  },
  untouchFormFields: fields => {
    dispatch(untouch('widgetForm', ...fields));
  },
  updateFormErrors: () => dispatch(updateSyncErrors('widgetForm', {}))
});

export default reduxForm({
  form: 'widgetForm',
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  touchOnBlur: false,
  validate
})(connect(mapStateToProps, mapDispatchToProps)(AccommodationDialog));
